import { z } from "zod";
/**
 * LAMA report dashboard widgets (ADR-0054) — the eight content sections of the
 * campaign report as `dashboard_instance` widget kinds.
 *
 * FOUR CARRY A PRESENTATION PARAM, FOUR ARE STILL `{}`-STRICT (PB-191 Task 19),
 * and each param that exists REPLACES A CONSTANT THE PROJECTOR ALREADY
 * HARDCODED — the movers' `.slice(0, 5)`, the roadmap's `ROADMAP_SIZE`, the two
 * trends' implicit "every wave". So none is a knob someone imagined a user might
 * want; each is a choice the code was making silently. The other four have no
 * variable presentation, and inventing one for symmetry would be worse than the
 * asymmetry.
 *
 * EVERY FIELD IS OPTIONAL, which is load-bearing: shipped instances hold
 * `params: {}`, re-validated on every PATCH including a title-only edit, so one
 * required field would make every existing report widget unpatchable.
 *
 * A param may narrow what is DISPLAYED; it may never change what is FETCHED. The
 * content is a campaign read resolved through the widget's template-owned data
 * connection, and data-selecting identifiers stay banned from widget params by
 * the dashboard contract.
 */
const EmptyParamsSchema = z.object({}).strict();
/**
 * How many entries a ranked list shows. Bounded above by what the projector can
 * SUPPLY, not by a round number — `top-movers` is sliced to five server-side, so
 * a higher limit would be a promise the data cannot keep.
 */
const listLimit = (max) => z.number().int().min(1).max(max).optional();
/**
 * How many of the most recent waves a trend plots. Lower bound 2 — a trend over
 * one point is not one. Upper bound 24, two years of monthly cadence.
 *
 * An earlier version of this said "and the widgets have a distinct single-wave
 * state", which is only half true: `lamaCategoryResults` does (the sparkline
 * becomes a spacer and the delta drops out), but `lamaMaturityTrend` has no
 * single-point branch at all — it would plot one dot. The bound is what keeps
 * that unreachable, so it is load-bearing rather than a nicety.
 */
const waveWindow = () => z.number().int().min(2).max(24).optional();
export const LamaReportOverviewParamsSchema = EmptyParamsSchema;
export const defaultLamaReportOverviewParams = {};
export const LamaMaturityProfileParamsSchema = EmptyParamsSchema;
export const defaultLamaMaturityProfileParams = {};
/** Plots the last `waveWindow` waves. Absent → every wave, the shipped default. */
export const LamaMaturityTrendParamsSchema = z
    .object({ waveWindow: waveWindow() })
    .strict();
export const defaultLamaMaturityTrendParams = {};
/** Plots the last `waveWindow` waves. Absent → every wave, the shipped default. */
export const LamaParticipationTrendParamsSchema = z
    .object({ waveWindow: waveWindow() })
    .strict();
export const defaultLamaParticipationTrendParams = {};
/**
 * The v3 hierarchy kinds (2026-09-10 ADR-0027 amendment) carry NO params:
 * which source a widget draws (baseline / responses / compare) is the host's
 * `?view=` URL state, applied display-only at render — never a persisted
 * parameter and never a data id (PEND2).
 */
export const LamaMaturityHierarchyParamsSchema = EmptyParamsSchema;
export const defaultLamaMaturityHierarchyParams = {};
export const LamaHierarchyResultsParamsSchema = EmptyParamsSchema;
export const defaultLamaHierarchyResultsParams = {};
/**
 * The host's `?view=` vocabulary for the hierarchy kinds. `compare` is the
 * default and needs both sources; a widget asked for a view its payload cannot
 * show falls back (see `effectiveHierarchyView` in @playbookai/ui) and says so.
 * URL state only — never persisted, never a viewer configuration write.
 */
export const LAMA_HIERARCHY_VIEWS = [
    "baseline",
    "responses",
    "compare",
];
export const LamaHierarchyViewSchema = z.enum(LAMA_HIERARCHY_VIEWS);
export function isLamaHierarchyView(value) {
    return (typeof value === "string" &&
        LAMA_HIERARCHY_VIEWS.includes(value));
}
export const LamaPillarResultsParamsSchema = EmptyParamsSchema;
export const defaultLamaPillarResultsParams = {};
export const LamaTeamHeatmapParamsSchema = EmptyParamsSchema;
export const defaultLamaTeamHeatmapParams = {};
/** Shows `limit` improved and `limit` slipped. Absent → 5, what the projector
 *  already slices to. */
export const LamaTopMoversParamsSchema = z
    .object({ limit: listLimit(5) })
    .strict();
export const defaultLamaTopMoversParams = {};
/** Shows `limit` priorities. Absent → 10, the projector's `ROADMAP_SIZE`. */
export const LamaPriorityRoadmapParamsSchema = z
    .object({ limit: listLimit(10) })
    .strict();
export const defaultLamaPriorityRoadmapParams = {};
//# sourceMappingURL=lamaReport.js.map